import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { DataGrid } from "@mui/x-data-grid";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import { useLocation } from "react-router-dom";
import SearchTable from "../../components/SearchTable";
import BackButton from "../../components/BackButton";

const WayaBankAuditReports = () => {
  const location = useLocation();
  const initialUserId = location.state?.userId || "";

  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchKey, setSearchKey] = useState(initialUserId);

  const { snackError } = useAuth();
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();

  const getAuditReports = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/audit-reporting/audit/admin/events/all";
      const params = {
        page: page - 1,
        size: pageSize,
        sortBy: "timestamp",
        sortDir: "desc",
      };
      
      if (searchKey) {
        url = "/audit-reporting/audit/events/search";
        params.userId = searchKey;
      }

      const response = await axiosPrivate.get(url, { params });

      const responseData = response?.data;
      
      // Parse content list from response
      let contentList = [];
      if (responseData) {
        if (Array.isArray(responseData)) {
          contentList = responseData;
        } else if (Array.isArray(responseData?.data?.content)) {
          contentList = responseData.data.content;
        } else if (Array.isArray(responseData?.data)) {
          contentList = responseData.data;
        } else if (Array.isArray(responseData?.content)) {
          contentList = responseData.content;
        }
      }

      // Parse total elements / count for pagination
      let totalElements = 0;
      if (responseData) {
        if (responseData?.data?.totalElements !== undefined) {
          totalElements = responseData.data.totalElements;
        } else if (responseData?.totalElements !== undefined) {
          totalElements = responseData.totalElements;
        } else if (responseData?.data?.totalRows !== undefined) {
          totalElements = responseData.data.totalRows;
        } else if (responseData?.data?.total !== undefined) {
          totalElements = responseData.data.total;
        } else if (responseData?.total !== undefined) {
          totalElements = responseData.total;
        } else {
          totalElements = contentList.length;
        }
      }

      setDetails(contentList);
      setTotalRows(totalElements);
    } catch (error) {
      if (error?.response?.status === 403) {
        snackError("Sorry, Not Authorized to view Audit Reports");
      } else {
        console.error("Error fetching audit reports:", error);
        snackError(error?.response?.data?.message || "Failed to fetch audit reports.");
      }
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, page, pageSize, searchKey, snackError]);

  useEffect(() => {
    getAuditReports();
  }, [getAuditReports]);

  const handleSearch = ({ searchKey: key }) => {
    setSearchKey(key ? key.trim() : "");
    setPage(1);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {row.id || row.activityId || row.eventId || "N/A"}
        </Typography>
      ),
    },
    {
      field: "activity",
      headerName: "Activity / Action",
      width: 220,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {row.activity || row.action || row.activityName || row.actionName || "N/A"}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      width: 300,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {row.description || row.message || row.details || row.narration || "N/A"}
        </Typography>
      ),
    },
    {
      field: "performedBy",
      headerName: "Performed By",
      width: 200,
      renderCell: ({ row }) => (
        <Typography variant="body2" color={colors.orangeAccent[300]}>
          {row.performedBy || row.username || row.email || row.userId || row.initiator || "N/A"}
        </Typography>
      ),
    },
    {
      field: "ipAddress",
      headerName: "IP Address",
      width: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {row.ipAddress || row.ip || row.deviceId || "N/A"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: ({ row }) => {
        const val = row.status || row.activityStatus || "";
        const isSuccess =
          val.toUpperCase() === "SUCCESS" ||
          val.toUpperCase() === "SUCCESSFUL" ||
          val.toUpperCase() === "TRUE" ||
          val.toUpperCase() === "APPROVED";
        return (
          <Typography
            variant="body2"
            color={isSuccess ? colors.greenAccent[400] : colors.redAccent[400]}
            sx={{ fontWeight: "bold" }}
          >
            {val || "N/A"}
          </Typography>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      width: 200,
      renderCell: ({ row }) => {
        const dateVal = row.createdAt || row.timestamp || row.dateCreated || row.createdDate;
        if (!dateVal) return "N/A";
        try {
          return new Date(dateVal).toLocaleString();
        } catch {
          return dateVal.toString();
        }
      },
    },
  ];

  return (
    <Box m="20px">
      {location.state && <BackButton path={-1} />}
      <Box
        display={smScreen ? "flex" : "block"}
        flexDirection={smScreen ? "row" : "column"}
        justifyContent={smScreen ? "space-between" : "start"}
        alignItems={smScreen ? "center" : "start"}
        m="10px 0"
      >
        <Header title="AUDIT REPORTS" subtitle="WayaBank System Audit and Activity Trail" />
        
        <Box display={"flex"} justifyContent={"space-between"}>
          <SearchTable
            handleSearch={handleSearch}
            placeholder="Search activities / users..."
            initialValue={searchKey}
          />
        </Box>
      </Box>

      {/* GRID */}
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Box
          m="10px"
          width="100%"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .name-column--cell": {
              color: colors.orangeAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.redAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.redAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.orangeAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            loading={loading}
            getRowId={(row) => {
              return row.id || row.activityId || row.eventId || Math.random();
            }}
            rows={details || []}
            columns={columns}
            paginationMode="server"
            rowCount={totalRows}
            page={page - 1}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Box>
      </Grid>
    </Box>
  );
};

export default WayaBankAuditReports;
